/**
 * inventoryOrderingDisplay — the SHARED display vocabulary for the Inventory &
 * Ordering module (build plan §5), Lane 500.
 *
 * THREE surfaces read this module and no other: the ranked worklist (step 3,
 * this lane), the material dossier placement and the supplier dossier placement
 * (step 4, next). That is the whole point of it existing. If a material dossier
 * and the worklist phrased the same instruction differently, a buyer would be
 * looking at two different answers to "what should I do about 570240" — the
 * exact "two surfaces computed the same quantity two different ways" failure the
 * module was built to end (plan §11). Put every label and every sentence HERE.
 *
 * DISPLAY ONLY — the hard constraint
 * ----------------------------------
 * Every number these helpers touch arrives FINISHED from `helpers/inventory_ordering.py`.
 * Nothing here computes, derives, re-rounds into a different value, sums two
 * fields, or reconciles. Choosing a currency symbol and a thousands separator is
 * the entire job. If you find yourself doing arithmetic in this file, stop: the
 * answer belongs in the calculation layer, where every surface gets it at once.
 *
 * THE TWO DOLLARS ARE DIFFERENT MONEY, AND THIS IS THE BIT TO GET RIGHT
 * ---------------------------------------------------------------------
 * A `reduce_next_order_to` row carries two dollar figures that are NOT the same
 * quantity and must NEVER be added, averaged, or shown as one number:
 *
 *   cash_released           the capital already sitting ABOVE XMEK's own stated
 *                           ceiling on this cell — (on_hand − max) × avg_cost.
 *                           A POSITION. It is what the worklist RANKS on.
 *   evidence.cash_avoided_now   the value of inbound units this instruction says
 *                           not to receive — (due_in − action_qty) × avg_cost.
 *                           A cash-flow effect, and the money the buyer's next
 *                           click actually moves.
 *
 * On real PROD data (2026-07-27 snapshot) the worklist's #1 row is 570240 @ W51:
 * cash_released **$27,035.61**, cash_avoided_now **$130.10** — a 208× gap,
 * because 2,599 units are over a max of 521 while only 10 are inbound. A surface
 * that renders "$27,035.61" beside "cancel this order" tells the buyer that
 * cancelling ten units frees twenty-seven thousand dollars. It does not. Both
 * figures are correct; showing the WRONG one for the instruction is what would
 * be wrong.
 *
 * Lane 526 resolved this into `moveValue()` below: the row shows ONE dollar,
 * SELECTED by which money the instruction actually moves — the over-max position
 * on a delay row, the stopped inbound spend on a cut row. Never summed, never
 * averaged, never derived. (Lane 521 rendered both in two labelled columns; Ryan
 * asked for the sub-labels to come off — *"just have it just be the number"* —
 * and a bare pair of unexplained dollars is worse than one correct one.)
 *
 * WHY THE IMPERATIVE LIVES HERE AND THE EVIDENCE CLAUSE DOES NOT
 * --------------------------------------------------------------
 * The calculation layer already authors `row.reason` — a good evidence clause
 * ("2,599 on hand against a max of 521 with 10 already on order"). It is the WHY
 * and it is rendered verbatim, never paraphrased. What it does not carry is an
 * executable imperative: its tail for the case above reads "receive no more than
 * 0", which is arithmetically perfect and is not something a buyer can act on.
 * `actionSentence` supplies the DO; `row.reason` supplies the BECAUSE. Hard rule
 * 7: an instruction rung is judged on whether a buyer could act on what it says.
 */

// --- vocabulary, frozen to the calculation layer's own strings -------------
// These keys are `helpers/inventory_ordering.py`'s module constants. A key that
// drifts renders its raw wire value rather than a wrong label (see `labelFor`).

export const ACTION_LABELS = {
  reduce_next_order_to: 'Cut inbound order',
  hold_until: 'Delay next order',
  increase_next_order_to: 'Order up',
  no_action: 'No action',
  insufficient_data: 'Not enough data',
};

export const POSITION_LABELS = {
  above_max: 'Above max',
  inside_band: 'In band',
  below_min: 'Below min',
  no_policy_set: 'No max set',
};

// `unknown` is 97.5% of actionable rows on real PROD data and is a FIRST-CLASS
// value, not a gap (hard rule 2 — never assume price holds). It must read as a
// real state a buyer understands, never as an empty cell that looks broken.
export const PRICE_SIGNAL_LABELS = {
  holds: 'Holds at volume',
  improves_at_volume: 'Cheaper in bulk',
  worsens_at_volume: 'Dearer in bulk',
  unknown: 'Not enough comparable orders',
};

export const PRICE_SIGNAL_TONE = {
  holds: 'neutral',
  improves_at_volume: 'warn',   // a reason NOT to draw down — Ryan's ENFORCE
  worsens_at_volume: 'good',    // a reason the drawdown is free
  unknown: 'muted',
};

export const SAFETY_LABELS = {
  passes: 'Checked',
  unverified: 'Not fully checked',
  would_breach_min: 'Would breach min',
  would_breach_lead_time: 'Would breach lead time',
};

export const SAFETY_TONE = {
  passes: 'good',
  unverified: 'muted',
  would_breach_min: 'bad',
  would_breach_lead_time: 'bad',
};

// `suppression_reason()`'s named refusals. Every branch that declines to
// instruct is named — the calculation layer refuses an `other` bucket, and so
// does this, because an unexplained shortfall reads as a missing number.
// Lane 521 copy pass (Ryan's QA 2026-07-29). He could not parse two strings on
// this page, and the shared cause was engineering vocabulary that had escaped
// onto a customer surface — "cell", "module", "drawdown", "instruct on". Those
// are swept HERE rather than at each call site, because three surfaces read this
// file and a fix applied to one of them would leave the other two speaking the
// old language. Genuine procurement terms a buyer already owns — reorder point,
// min, max, lead time, stockout — are deliberately KEPT: the problem was never
// that the page used domain words, it was that it used OUR words.
export const SUPPRESSION_LABELS = {
  beyond_the_dating_horizon: 'Stock will not run down for years',
  would_breach_safety: 'Using more of this stock would risk a stockout',
  no_recorded_usage: 'No recorded usage: this stock is not moving',
  no_floor_could_be_computed: 'No stated minimum and no measured lead time',
  site_mid_relocation: 'Warehouse mid-relocation: history is not continuous',
  stated_min_exceeds_stated_max: 'Stated min is higher than stated max',
  price_improves_at_volume: 'Larger orders price lower: holding off would cost more',
  other: 'No recommendation',
};

/**
 * The suppression buckets grouped into the three KINDS of thing they are.
 *
 * On real PROD data the gap between XMEK's own $3,004,832 above-max figure and
 * the $1,592,947 this module instructs on is **$1,411,884 — 47%**, and a flat
 * list of seven jargon labels renders that as seven ways the module declined to
 * help. Grouped, it reads as a second finding:
 *
 *   disposal   $890,409 (63% of the gap) — over-max capital consumption will
 *              never retire. Not an ordering problem and not this module's to
 *              solve; it reconciles against XMEK's own slow/obsolete $2,553,126.
 *   safety     $485,201 (34%) — the module found a move and refused it.
 *   withheld   $36,274 (3%)  — deliberate refusals (price, relocation, policy).
 *
 * EXHAUSTIVE BY TEST. Every key `suppression_reason()` can return must appear
 * here; a new Python bucket that this map does not know would otherwise vanish
 * from the page silently — the same failure the calc layer refused when it
 * declined to keep an `other` bucket. `test_inventory_ordering_display` pins
 * this map against the Python source.
 */
export const SUPPRESSION_GROUPS = {
  beyond_the_dating_horizon: 'disposal',
  no_recorded_usage: 'disposal',
  // ⚠️ MOVED OUT OF `disposal` (Ryan, 2026-07-30, round-9 decision 1) — this is
  // the defect Lane 529 measured and reported rather than fixed, because fixing
  // it moves a figure he had already read.
  //
  // The engine deliberately does NOT call this disposal, and its own sentence
  // for it contradicts the disposal heading outright: *"You have set no minimum
  // for this item and we have measured no lead time for the site, so there is no
  // floor to draw down to. Set a minimum, or ask for the lead time, before
  // acting."* That is a policy-and-data gap with two concrete remedies — using
  // the stock up may well clear it; we do not know where to stop — sitting under
  // a heading that says using it up will NOT clear it.
  //
  // Measured on real staging (2026-06-15, company 91) by
  // `backend/scripts/lane534_round9_decisions_preview.py`:
  //
  //   disposal BEFORE   1,535 rows   $968,058.83
  //   disposal AFTER    1,211 rows   $890,898.39
  //   no_floor            324 rows    $77,160.44
  //
  // The PARENT does not move: hero line 2 is $1,554,591.11 before and after, so
  // this redistributes money between blocks rather than changing the total. That
  // is asserted by the preview and pinned by test — a taxonomy edit that leaked
  // money out of the reconciliation would otherwise be invisible.
  no_floor_could_be_computed: 'no_floor',
  would_breach_safety: 'safety',
  price_improves_at_volume: 'withheld',
  site_mid_relocation: 'withheld',
  stated_min_exceeds_stated_max: 'withheld',
  // `other` is a defect signal, not an explanation. The calc layer asserts it is
  // empty; if it ever appears it must read as unexplained, never as a category.
  other: 'unexplained',
};

/**
 * ⚠️ THESE FOUR HEADINGS ARE STILL LANE-INVENTED AND STILL UNSIGNED — and Lane
 * 529 measured WHY the expected fix does not reach them.
 *
 * QA round 9: *"The three block headings still do not communicate… the third one
 * he could not interpret at all. He is asking what each one MEANS, not for
 * shorter words — so this is a definitional problem, not a copy trim. Whoever
 * takes it should be able to state, in one sentence each, what money is in the
 * bucket and what the buyer would do about it. If that sentence cannot be
 * written, the bucket is wrong."*
 *
 * Lane 525 authored exactly that sentence — but at TWO grains, and neither is
 * this one:
 *
 *   `meta.groups[k].definition`   the ENGINE's eight `worklist_group` buckets
 *                                 (worklist / no_purchase_history / set_aside /
 *                                 stockouts / disposal / suppressed /
 *                                 no_stated_max / no_action_needed)
 *   `meta.cash_released_gap.definitions[reason]`
 *                                 one per `suppression_reason` — the SEVEN keys
 *                                 `SUPPRESSION_LABELS` above is keyed by
 *
 * The four headings below are a FRONTEND-ONLY taxonomy that groups those seven
 * reasons into four kinds of thing (see `SUPPRESSION_GROUPS`). The engine does
 * not know it exists, so it authors no sentence for it.
 *
 * ⚠️ AND THE ONE NAME THEY SHARE DISAGREES. Measured on real staging
 * (2026-06-15, company 91) via `backend/scripts/lane529_integration_preview.py`:
 *
 *   page block   `disposal`  1,535 rows  $968,058.83
 *   engine group `disposal`  1,211 rows  $890,898.39
 *   difference    +324 rows   $77,160.44 — exactly `no_floor_could_be_computed`
 *
 * The engine deliberately does NOT call that reason disposal, and its own
 * sentence for it reads *"You have set no minimum for this item and we have
 * measured no lead time for the site… Set a minimum, or ask for the lead time,
 * before acting."* That is a policy-and-data gap, not a write-off — so pasting
 * `meta.groups.disposal.definition` onto this heading would have described 1,211
 * rows and mislabelled 324 of them. **That is a finding about the bucket, and it
 * is Ryan's to resolve** (his own hero figures are $968,058 / $427,879 /
 * $158,704, summing to $1,554,641, so moving a reason between blocks moves
 * numbers he has read and approved).
 *
 * WHAT LANE 529 DID INSTEAD: rendered the engine's PER-REASON sentence under
 * each reason row inside the block, where the keying is exact and no wording is
 * invented. Every reason in every block now states what money is in it and what
 * the buyer would do — the definitional thing that was asked for — at the grain
 * the engine actually authored it.
 */
export const SUPPRESSION_GROUP_LABELS = {
  disposal: 'Using it up will not clear this',
  safety: 'A move exists, but it would risk a stockout',
  withheld: 'Held back on purpose',
  // NEW (Lane 534, item 1). UNSIGNED like the other four, but written against
  // the engine's own sentence rather than invented: that sentence names two
  // missing inputs and two remedies, and this heading states the consequence of
  // them being missing. It is the one heading here whose meaning is checkable —
  // the engine's definition renders directly beneath it (see `showsGroupNote`),
  // so the two can be read together.
  no_floor: 'We cannot tell how far down this can safely come',
  unexplained: 'Not yet categorised',
};

/** Display order for the refusal blocks, biggest kind of thing first.
 *
 *  RENDERED AS A FLOOR, NOT AS A WHITELIST. Lane 525 is adding suppression
 *  buckets (stockouts, no stated maximum, no purchase history). A group key this
 *  list does not know must still appear — filtering the render down to this
 *  array would make a new bucket's dollars vanish from the page silently, which
 *  is precisely the failure the calc layer refused when it declined to keep an
 *  `other` bucket. Known groups first, in this order; anything else after, in
 *  descending dollars, labelled by `labelFor` so it renders as its raw wire
 *  value rather than as nothing.
 */
export const GROUP_ORDER = Object.freeze([
  // Biggest kind of thing first, on real staging dollars: disposal $890,898 /
  // safety $427,829 / withheld $158,703 / no_floor $77,160. `unexplained` stays
  // last because it is a defect signal rather than a size.
  'disposal', 'safety', 'withheld', 'no_floor', 'unexplained',
]);

/** Known groups in their declared order, then any group this file has never
 *  heard of, largest first. Pure ordering — no dollars are combined here. */
export function orderGroups(groupKeys) {
  const known = GROUP_ORDER.filter((g) => groupKeys.includes(g));
  const unknown = groupKeys.filter((g) => !GROUP_ORDER.includes(g));
  return known.concat(unknown);
}

/**
 * The engine's own sentence for one refusal reason, or null.
 *
 * SELECTION FROM THE PAYLOAD, never a local string. `meta.cash_released_gap
 * .definitions` is keyed by `suppression_reason` — the SAME key the block's
 * reason rows already render through `SUPPRESSION_LABELS` — so this is the one
 * grain where the engine's text and this page's structure line up exactly. A
 * reason the server sent no definition for renders none; the page never
 * substitutes a local sentence for a missing engine one, because a definition
 * this file wrote is precisely what produced headings Ryan could not read.
 */
export function reasonDefinition(gap, reason) {
  const defs = (gap && gap.definitions) || {};
  const d = defs[reason];
  return typeof d === 'string' && d.trim() ? d : null;
}

/**
 * The engine's own sentence for a whole `worklist_group`, or null.
 *
 * ⚠️ ONLY VALID WHERE A SECTION OF THIS PAGE **IS** ONE OF THOSE GROUPS. Two
 * are, and it was verified in `routes/inventory_ordering.py` rather than
 * assumed — both lists are literally `by_group[...]` slices:
 *
 *   `data.set_aside`     === `by_group[GROUP_SET_ASIDE]`  -> the set-aside table
 *   `data.replenishment` === `by_group[GROUP_STOCKOUTS]`  -> the below-minimum
 *                                                            section
 *
 * The refusal BLOCKS are not — see the note above `SUPPRESSION_GROUP_LABELS`.
 * Do not reach for this to label them; the memberships differ by 324 rows and
 * $77,160 on real data, so it would put a sentence on a bucket it does not
 * describe.
 */
export function groupDefinition(meta, groupKey) {
  const g = ((meta && meta.groups) || {})[groupKey];
  const d = g && g.definition;
  return typeof d === 'string' && d.trim() ? d : null;
}

/**
 * Should the block still show its own lane-written orientation note?
 *
 * The note exists to explain a GROUPING — why these several refusals are one
 * kind of thing. A block holding exactly ONE reason has no grouping to explain,
 * and once that reason's engine sentence renders directly beneath the heading
 * the note restates it: on real data `safety` is the single reason
 * `would_breach_safety`, whose note and whose engine definition say the same
 * thing twice, one line apart. That is the failure the `withheld` note's own
 * comment already records — *"a note that repeats the label above it spends a
 * line saying nothing"*.
 *
 * GUARDED ON THE DEFINITION BEING PRESENT, not merely on the count: against a
 * payload that predates Lane 525 there is no engine sentence to supersede it,
 * and suppressing the note would leave the block with no prose at all.
 */
export function showsGroupNote(reasons, gap) {
  const list = reasons || [];
  if (list.length !== 1) return true;
  return !reasonDefinition(gap, list[0]);
}

// --- WHERE THE HEADLINE SITS (Lane 542, Ryan's QA round 10 decision b) -----

/**
 * The phrase that follows the count in one breakdown row. The COUNT is rendered
 * separately from `groups[k].rows`, so no string here carries a number.
 *
 * ⚠️ THESE ARE LANE-WRITTEN AND UNSIGNED, like every other heading on this page.
 * They are deliberately short because the row already carries a dollar and a
 * share; the engine's own full sentence for the group is one hover away
 * (`groupDefinition(meta, key)` renders as the row's `title`), so the reader who
 * wants the definition gets the ENGINE's words, not these.
 *
 * ALL EIGHT GROUPS ARE LISTED even though only three can carry a
 * `cash_released` on today's data. Same reasoning as `GROUP_ORDER` above: a
 * label list that only knows the groups we happen to see is how a new bucket's
 * money renders as a raw wire key — or worse, gets filtered out.
 */
export const WORKLIST_GROUP_LABELS = {
  worklist: 'moves ranked below',
  set_aside: 'smaller moves set aside',
  no_purchase_history: 'moves on materials with no purchase history',
  stockouts: 'items below your stated minimum',
  disposal: 'items consumption will not clear',
  suppressed: 'items where a move exists but we decline to instruct',
  no_stated_max: 'items with no stated maximum',
  no_action_needed: 'items already inside your band',
};

/**
 * THE DECOMPOSITION OF `cash_released_total`, as rows a surface can render.
 *
 * Ryan, QA round 10: *"the 98 moves do not deliver the $1.7M"* — and he is
 * right. On the live payload the queue's 98 rows carry **$563,771 of the
 * $1,723,408, a third of it**; the rest sits in the set-aside and no-purchase-
 * history groups. The page used to state the total and the count side by side,
 * which reads as cause and effect. This is the fix.
 *
 * PURE SELECTION. Every dollar, count and share is a server field
 * (`meta.groups[k].cash_released` / `.rows` / `.cash_released_share_pct`).
 * Nothing here adds, divides or derives — this page performs no arithmetic, and
 * the one time a surface tried to compute a coverage figure for this exact
 * headline it produced a number that was not a subset of its own denominator
 * (see the ⚠️ block above the component's `return`).
 *
 * ⚠️ IT MUST NOT SUM THE ROW LISTS INSTEAD, and that is not doctrine, it is
 * arithmetic: the route CAPS the context lists, so `data.set_aside` holds 100 of
 * 3,185 rows. Summing it client-side yields **$190,977 against a true
 * $943,051** — a 4.9x understatement rendered as fact.
 *
 * EVERY GROUP THAT CARRIES ONE, never a hardcoded three. `worklist_group` can
 * route an above-max row with a live `cash_released` into `no_action_needed`,
 * so a fixed three-row render could drop money silently on other data. Ordering
 * is the worklist first — those are the rows on THIS page, the ones the reader
 * can act on now — then the rest largest first.
 *
 * Returns `[]` when the server sends no `cash_released` on any group (an older
 * payload), and the caller renders nothing rather than a half-built block.
 */
export function cashReleasedSplit(meta) {
  const groups = (meta && meta.groups) || {};
  const parts = Object.keys(groups)
    .filter((k) => {
      const v = groups[k] && groups[k].cash_released;
      return v !== null && v !== undefined && !Number.isNaN(Number(v));
    })
    .map((k) => ({
      key: k,
      rows: groups[k].rows,
      dollar: groups[k].cash_released,
      sharePct: groups[k].cash_released_share_pct,
      label: labelFor(WORKLIST_GROUP_LABELS, k),
      definition: groupDefinition(meta, k),
    }));
  parts.sort((a, b) => {
    if (a.key === 'worklist') return -1;
    if (b.key === 'worklist') return 1;
    return (b.dollar || 0) - (a.dollar || 0);
  });
  return parts;
}

/** What `cash_released` IS, in the calculation layer's own words: *"the capital
 *  currently sitting above their own stated ceiling on this cell, which this
 *  instruction is working to release"*. Echoed rather than re-defined — a
 *  definition this file invented is exactly what produced headings Ryan could
 *  not read. UNSIGNED. */
export const CASH_RELEASED_BASIS_NOTE =
  'Each figure is the capital sitting above your own stated maximum on those '
  + 'rows. It is one measure, so they add up.';

/**
 * WHY THE USAGE FIGURE IS WHAT IT IS (Ryan, round 10 decision d — *"one or two
 * sentences, not the multiparagraph bullshit"*).
 *
 * Verified at source rather than paraphrased from the column: the engine's
 * `safety_daily_usage` is `max(usage_12mo/365, usage_3mo/91.25)` — the FASTER of
 * the two — and its own comment states the consequence this sentence carries:
 * *"every DATE this module emits (`hold_until`, and the back-inside-max date) is
 * computed from the safety rate"*. Measured across the 98 live rows: 53 are on
 * the trailing year, 45 on the recent quarter.
 *
 * UNSIGNED.
 */
export const USAGE_BASIS_NOTE =
  'Whichever rate is faster: the trailing year, or the last 3 months. Every '
  + 'date on the row is computed from it, so an item that has sped up is dated '
  + 'on the faster rate.';

export const SUPPRESSION_GROUP_NOTES = {
  disposal: 'Money tied up above your max that ordering differently cannot fix. '
    + 'This is a disposal or obsolescence question, and a different owner.',
  safety: 'We found a way to draw this stock down and chose not to recommend it, '
    + 'because the item is already at or below the level where you reorder.',
  // Does NOT restate its own heading — a note that repeats the label above it
  // spends a line saying nothing, and made the two indistinguishable on the page.
  withheld: 'Either larger orders price lower, so drawing down would cost more '
    + 'per unit; or the warehouse is mid-relocation; or the stated min and max '
    + 'contradict each other.',
  // FALLBACK ONLY, and it will not render against a current payload. `no_floor`
  // holds exactly ONE reason, so `showsGroupNote` suppresses this the moment the
  // engine's own sentence for that reason arrives — which is the better text and
  // is the whole point of the split. This exists for a payload that predates
  // Lane 525, where suppressing it would leave the block with no prose at all.
  no_floor: 'No minimum is set and no lead time has been measured, so there is '
    + 'no level to draw this stock down to.',
  unexplained: 'A reason this page does not recognise. Please report it.',
};

/** Wire value -> label, falling back to the RAW value. A new calc-layer enum
 *  member then shows up as itself, which is visible and honest; inventing a
 *  friendly label for a value we do not recognise is not. */
export function labelFor(map, value, fallback = 'Not available') {
  if (value === null || value === undefined || value === '') return fallback;
  return map[value] || String(value);
}

// --- formatting ------------------------------------------------------------

/** Whole dollars. Every value arrives already rounded by the calc layer. */
export function formatUsd(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not available';
  const n = Number(value);
  const s = Math.abs(n).toLocaleString('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
  return n < 0 ? `−${s}` : s;
}

/** Quantities: no trailing .0 on whole numbers — mirrors the calc layer's own
 *  `_qty()` so a quantity reads identically in the sentence and in the cell. */
export function formatQty(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not available';
  const n = Number(value);
  return Number.isInteger(n)
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Quantities rounded to a WHOLE unit — EVERY quantity COLUMN on the worklist.
 *
 * ⚠️ THIS WAS SCOPED TO THE USAGE COLUMN ALONE AND THAT WAS WRONG. Ryan asked
 * for *"whole numbers, no decimals"* while pointing at usage; the first cut read
 * that as a one-column instruction, and the render still showed `16,118.91` on
 * hand and `15,758.91` projected — 12 cells across the 98 rows. A stock count of
 * 16,118.91 reads as noise whichever column it sits in. **One formatter, every
 * quantity column**, so a future column cannot drift and this cannot be
 * half-fixed a second time.
 *
 * WHERE IT APPLIES: on hand, on order, projected, your max, usage / mo — and the
 * same columns in the refusal blocks' mini tables.
 *
 * WHERE IT DELIBERATELY DOES NOT: the money column (dollars have their own
 * formatter), lead time (`formatDays` already rounds), the dates, the hero card
 * figures, and material numbers — several of which legitimately contain a dot
 * (`TCAT-2.000-2.562-.330 OIL SEAL`).
 *
 * WHY `formatQty` STILL EXISTS. It mirrors the calculation layer's own `_qty()`,
 * so a quantity reads identically in a cell and inside a sentence the ENGINE
 * composed. It still formats `reorder_point_qty` in the reorder-date note, which
 * is genuinely fractional (648.537) and is a computed reorder point rather than
 * a counted quantity. Checked before splitting them: `due_in_po_qty`, `max_qty`
 * and `action_qty` are integer on all 514 live rows, so no cell can disagree
 * with the sentence beside it today.
 *
 * Rounds rather than truncates — `16,118.91` becomes `16,119` — and keeps
 * thousands separators. A rate genuinely below half a unit a month renders `0`,
 * which is honest for a column headed "per mo" and is NOT the absent state; that
 * still renders the cell's `absentNote`.
 */
export function formatQtyWhole(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not available';
  return Math.round(Number(value)).toLocaleString('en-US');
}

/** 'YYYY-MM-DD' -> 'Jan 19, 2027'. Parsed as CALENDAR parts, never through
 *  `new Date('YYYY-MM-DD')`, which is UTC-midnight and renders the previous day
 *  in US timezones — the bug Lane 431 fixed on the Opportunities surface. */
export function formatDate(value) {
  if (!value) return 'Not available';
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(value);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

export function formatDays(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'Not available';
  const n = Number(value);
  return `${Math.round(n).toLocaleString('en-US')}d`;
}

// --- THE STORY, NOW AS COLUMNS (Lane 521 -> Lane 526) ----------------------

/**
 * The story Ryan asked this page to tell, one beat per TABLE COLUMN.
 *
 * WHY THIS REPLACED `storyBeats` RATHER THAN JOINING IT
 * -----------------------------------------------------
 * Lane 521 rendered these beats as a labelled ladder inside each row's card.
 * Lane 523 drew them as table columns instead, and that mockup is the first
 * version of this page Ryan read successfully — he walked material 362044
 * unaided: *"910 on hand, zero on order… if we use 58 a month, until we're 8.8
 * months in the max, we'll probably order again. Don't order again until May."*
 * QA round 9 is explicit: **do not redesign the row sequence.**
 *
 * So the sequence survives verbatim and only its geometry changed. Keeping
 * `storyBeats` alongside this would leave TWO vocabularies for one story in the
 * module whose entire reason for existing is that there is only ever one (see
 * the header note) — so it was replaced, not duplicated.
 *
 * The mockup splits what the ladder folded together: "on order" and the
 * projected position get their own columns instead of riding as a note on the
 * on-hand beat, and the order-by date moves into the recommendation cell where
 * the instruction is. Lead time gets its own column per QA round 9 — it was
 * buried inside the recommendation sentence.
 *
 * SELECTION AND LABELLING ONLY, unchanged. Every value arrives finished from
 * `helpers/inventory_ordering.py`. Nothing here divides, converts a unit, or
 * synthesises a figure the server does not hold.
 *
 * HONEST ABSENCE, unchanged, and it still carries the CAUSE rather than a blank.
 * Measured on the 2026-07-27 PROD snapshot: 35.4% of all actionable rows have a
 * measured lead time, but 64.3% of THE QUEUE does — the materiality floor
 * removes the low-value tail, and that tail is where the thin history sits. So
 * absence is the common case on the estate and the minority case on this page,
 * and it is stated either way. An absent cell reads as a stated absence, never
 * as an empty cell that looks broken and never as an invented number.
 */
export function rowCells(row) {
  const ev = (row && row.evidence) || {};
  const has = (v) => v !== null && v !== undefined && !Number.isNaN(Number(v));
  const pos = (v) => has(v) && Number(v) > 0;

  // The drawdown projection, and the ONE place a future uncapped variant lands.
  //
  // `days_to_max` / `back_inside_max_date` are bounded by the calc layer's
  // 730-day horizon on purpose — beyond it "a date is not a projection, it is
  // arithmetic overflow dressed as one". The mockup showed "8.3 yrs est." for
  // such a row, which was MOCKUP arithmetic and does not exist in the engine.
  // Lane 525 adds an uncapped *display* projection under `projected_*`; these
  // are ITS field names, read from its in-flight endpoint rather than guessed.
  // The capped fallback stays, so if that lane's shape moves again the cell
  // degrades to the honest horizon statement rather than to a blank.
  const untilDate = ev.projected_back_inside_max_date || ev.back_inside_max_date;
  const untilDays = has(ev.projected_days_to_max) ? ev.projected_days_to_max
    : ev.days_to_max;

  // WHY the projection is missing, said plainly. Three different causes collapse
  // into one null in the calc layer (no usage rate, no stated max, or past the
  // horizon); a single "not available" would let the reader assume the wrong one.
  let untilAbsent = 'past the 2-year horizon';
  if (!pos(ev.planning_monthly_usage)) untilAbsent = 'no usage recorded';
  else if (!pos(ev.max_qty)) untilAbsent = 'no max set';

  return [
    {
      key: 'on_hand', label: 'On hand', numeric: true,
      value: has(ev.on_hand_qty) ? formatQtyWhole(ev.on_hand_qty) : null,
    },
    {
      key: 'on_order', label: 'On order', numeric: true,
      value: has(ev.due_in_po_qty) ? formatQtyWhole(ev.due_in_po_qty) : null,
    },
    {
      // The position that actually draws down, and the number the dates rest on.
      // NOTE FOR A LATER LANE: this is the server's `net_position_qty`, which is
      // on-hand + on-order MINUS committed demand, whereas the drawdown date is
      // computed from on-hand + on-order alone. On every one of the 114 real
      // queue rows committed demand is zero, so the two agree and this column
      // reconciles with the two beside it. If a tenant ever carries due-out on a
      // queue row they diverge, and the right fix is a calc-layer field — not a
      // subtraction here.
      key: 'projected', label: 'Projected', numeric: true, strong: true,
      value: has(ev.net_position_qty) ? formatQtyWhole(ev.net_position_qty) : null,
    },
    {
      key: 'max', label: 'Your max', numeric: true,
      value: pos(ev.max_qty) ? formatQtyWhole(ev.max_qty) : null,
      absentNote: 'none set',
    },
    {
      key: 'usage', label: 'Usage / mo', numeric: true,
      // WHOLE UNITS, like every other quantity column here. See
      // `formatQtyWhole` — including why scoping it to this column alone was
      // the wrong reading of Ryan's instruction.
      value: pos(ev.planning_monthly_usage)
        ? formatQtyWhole(ev.planning_monthly_usage) : null,
      // Which rate, said plainly: every date on the row is computed from THIS
      // number, so when the recent quarter runs hotter than the year the buyer
      // is told rather than left wondering why the figure looks high.
      sub: ev.usage_basis === 'recent_quarter' ? 'last 3 months' : null,
      absentNote: 'none recorded',
    },
    {
      key: 'until_under_max', label: 'Until under max',
      value: untilDate ? formatDate(untilDate) : null,
      sub: has(untilDays) ? `${formatQty(untilDays)} days` : null,
      absentNote: untilAbsent,
    },
    {
      // QA round 9: *"Lead time gets its own column."* It was buried in the
      // recommendation cell, where it read as a footnote to the instruction
      // rather than as one of the numbers that produces it.
      key: 'lead_time', label: 'Lead time',
      value: has(row && row.lead_time_days) ? formatDays(row.lead_time_days) : null,
      sub: has(row && row.lead_time_days) && pos(ev.lead_time_po_count)
        ? `${formatQty(ev.lead_time_po_count)} receipts` : null,
      absentNote: 'not measured',
    },
  ];
}

/**
 * THE ONE DOLLAR THE ROW SHOWS — a SELECTION between two server figures, never
 * a sum, never a derivation.
 *
 * The row carries two dollars that are different money and must never be added
 * (see the module header): `cash_released` is the capital already above XMEK's
 * ceiling — a POSITION — and `evidence.cash_avoided_now` is the value of inbound
 * units the instruction stops — a CASH-FLOW EFFECT. On the real #1 row they are
 * $27,036 and $130, a 208x gap.
 *
 * The column is headed "What the move saves", so it shows the money THIS
 * INSTRUCTION MOVES: a delay row frees the over-max capital as stock draws down;
 * a cut-inbound row stops the inbound spend. That is the mockup's own selection
 * (its #2 row shows $34,229 on a cut row, which is the avoided figure, against
 * an over-max position of about $15,000 on the same cell) and it is the fix for
 * the hazard the header names — rendering $27,036 beside "cancel ten units"
 * tells the buyer that cancelling ten units frees twenty-seven thousand dollars.
 *
 * THE CALCULATION LAYER OWNS THIS SELECTION WHEN IT SENDS ONE. Lane 525 added
 * `row_move_value()` and ships the result as `move_value` / `move_value_basis`;
 * that is preferred here whenever it arrives, so the number and its provenance
 * come from the one place every surface reads. The local selection below is the
 * FALLBACK for a payload that predates it, and it picks the same field for the
 * same action — deliberately, so the two cannot disagree on the value.
 *
 * ⚠️ `move_value_basis` IS LOAD-BEARING AND THIS SURFACE DOES NOT YET ACT ON IT.
 * Lane 525's own note: a `hold_until` figure is `not_reordered_proxy` — the size
 * of the deferred order cannot be computed (the feed carries no next-unplaced-
 * order object), so the over-max capital stands in for it — and *"any surface
 * rendering this must not present it as an exact saving"*. This column is headed
 * "What the move saves" and 108 of the 114 real rows are holds, so the header
 * currently overclaims on nearly all of them. The basis is carried to the DOM so
 * a treatment has a hook and a test can see it; **choosing that treatment is a
 * copy decision that collides with Ryan's "just have it just be the number" and
 * is escalated, not settled here** (handover §4.3).
 *
 * ⚠️ SEAM WITH RANKING (Lane 525 owns the order): the queue is RANKED on
 * `cash_released`, so on a cut-inbound row the displayed figure is not the
 * ranking key and the column need not descend monotonically. 6 of the 114 real
 * rows are cut-inbound rows. Flagged, not fixed here.
 */
export function moveValue(row) {
  if (!row) return { value: null, basis: null };
  if (row.move_value !== undefined) {
    return { value: row.move_value, basis: row.move_value_basis || null };
  }
  const ev = row.evidence || {};
  if (row.action === 'reduce_next_order_to') {
    return { value: ev.cash_avoided_now, basis: 'not_received' };
  }
  if (row.action === 'hold_until') {
    return { value: row.cash_released, basis: 'not_reordered_proxy' };
  }
  return { value: row.cash_released, basis: 'none' };
}

// --- THE INSTRUCTION -------------------------------------------------------

/**
 * The imperative a buyer reads and acts on, split into its three pieces so the
 * value the eye should land on can be emphasised. ONE definition; `actionSentence`
 * below is a thin join of it, so the plain string and the rendered cell can never
 * drift into two different instructions.
 *
 * Renders only values the calculation layer already computed. The judgement it
 * makes is linguistic, not numeric: which verb, and which of the row's finished
 * numbers belongs inside the sentence so the instruction is executable.
 *
 * The `action_qty === 0` case is the one that earns its keep. The calc layer's
 * own clause ends "receive no more than 0"; on real PROD data that is the top
 * three rows of the worklist. "Receive no more than 0" is not an instruction a
 * buyer can carry out — "Don't receive the 1,997 on order" is the same fact
 * stated as a next move.
 *
 * ⚠️ WORDING PENDING RYAN (Lane 526). These strings are the Lane 523 mockup's,
 * adopted because that mockup is the first version of this page he read
 * successfully. They REPLACE Lane 521's wording ("Hold off reordering until
 * about May 1, 2027." / "Cancel or push out the inbound order (1,997 units) —
 * $34,229 not spent now."). The trailing dollar came out because the "What the
 * move saves" column now carries it, and repeating it inside the sentence was
 * the same number twice on one row. Every string here is in the before/after
 * table in the lane's handover and is unsigned until Ryan says otherwise.
 */
export function actionParts(row) {
  if (!row) return null;

  // *** THE GUARD, and it is the whole reason this function is narrow. ***
  //
  // `action` carries TEN distinct meanings on `no_action` alone, and `reason` is
  // the only field that separates them: "no max set", "site mid-relocation",
  // "drawing down further would risk a stockout", "no drawdown recommended", and
  // — the dangerous one — "Do not delay the next order" on a row that is short
  // once committed demand ships. An imperative keyed on `action` renders "No
  // action needed" for that last case: the OPPOSITE instruction.
  //
  // Measured read-only on PROD (company 91, 2026-07-27 snapshot): **1,297 rows /
  // $516,447.72 of over-max capital** are `no_action` rows whose reason gives a
  // contrary instruction. That is the hard-rule-7 failure this rung has now had
  // four times — arithmetic impeccable, instruction wrong — and it would have
  // been invisible to every test that checks a number.
  //
  // The same measurement is what makes the actionable case SAFE, so this is a
  // boundary and not a retreat: `hold_until`, `reduce_next_order_to` and
  // `increase_next_order_to` have exactly **one** reason shape each across all
  // 4,644 actionable rows, and **zero** of them contradict their verb.
  //
  // So: an imperative ONLY where the verb is unambiguous. Everywhere else the
  // surface renders `row.reason` verbatim — the calc layer authors that string
  // inside hard rule 7's guards, and it is not this file's to restate.
  if (!isActionable(row)) return null;

  const ev = row.evidence || {};
  const dueIn = ev.due_in_po_qty;
  const hasDueIn = dueIn !== null && dueIn !== undefined;

  switch (row.action) {
    case 'reduce_next_order_to': {
      if (Number(row.action_qty) === 0) {
        return hasDueIn
          ? { lead: 'Don’t receive the ', strong: formatQty(dueIn), tail: ' on order' }
          : { lead: 'Don’t receive the order already placed', strong: '', tail: '' };
      }
      return {
        lead: 'Receive no more than ',
        strong: formatQty(row.action_qty),
        tail: hasDueIn ? ` of the ${formatQty(dueIn)} on order` : '',
      };
    }

    case 'hold_until':
      return row.action_date
        ? { lead: 'Don’t reorder until ', strong: formatDate(row.action_date), tail: '' }
        : { lead: 'Don’t reorder yet', strong: '', tail: '' };

    case 'increase_next_order_to':
      return { lead: 'Order up to ', strong: formatQty(row.action_qty), tail: ' units' };

    // `no_action` and `insufficient_data` are DELIBERATELY absent — the guard
    // above returns before reaching here. Adding a case for either reintroduces
    // the 1,297-row defect. If you are here to add one, read the guard first.
    default:
      return null;
  }
}

/**
 * THE POLICY FINDING BEHIND A ROW WHOSE TWO DATES READ BACKWARDS, or null.
 *
 * Ryan, 2026-07-30 (round-9 decision 4): *"fix the underlying setting — not just
 * explain it."* The recommendation put to him was to disclose that the dates
 * look odd; he went further, so this says what is wrong AND what the number
 * would need to be.
 *
 * WHAT THE READER IS LOOKING AT. On 18 of the 98 live worklist rows the
 * "Don't reorder until" date precedes the "Until under max" date — worst 105
 * days apart (378219 @ W31). Both come from the same shelf at the same rate and
 * differ only in what they subtract, so the reorder date leads exactly when the
 * reorder point sits above the stated maximum. Arithmetically inevitable, and a
 * real statement about the customer's own settings: the cell hits "time to
 * reorder" while still officially overstocked, every cycle.
 *
 * THREE THINGS THIS DELIBERATELY DOES NOT DO
 * ------------------------------------------
 * 1. **It does not compute.** Every figure is the engine's, already on the row:
 *    `max_qty` is the "Your max" column, `lead_time_days` is the "Lead time"
 *    column rendered through the SAME formatter, and `reorder_point_qty` is the
 *    one number the sentence introduces — which is the finding. The
 *    above-max test itself is the calculation layer's
 *    (`evidence.reorder_point_above_max`), not a comparison made here.
 * 2. **It does not claim we changed anything.** The maximum lives in XMEK's
 *    feed. "Raise the max above that" is a recommendation to the reader.
 * 3. **It never fires on a row with no measured lead time.** 34 of the 98
 *    worklist rows have none, and without one `rate x lead_time` does not exist,
 *    so a required figure would be invented. The `lead_time_demand` basis
 *    guarantees a measured lead time (the engine derives it through
 *    `breach_value`, which requires `reorder_basis == "min_qty_and_lead_time"`),
 *    and the explicit guards below make that a property of this function rather
 *    than a fact about the engine that a caller has to remember.
 *
 * IT REQUIRES BOTH DATES TO BE ON THE ROW, and that guard is not paranoia — it
 * closes a defect this function had. The sentence explains the RELATIONSHIP
 * between two dates the reader can see, so it must not render where only one of
 * them exists. `reorder_point_above_max` is a property of the POLICY and the
 * engine stamps it on every row shape, including `reduce_next_order_to`, whose
 * own branch handles this exact case (`receive_ceiling_basis:
 * "reorder_point_above_max"`). A cut row shows no reorder date at all, so
 * "…so this reorders before it is back under max" would be describing something
 * not on the page. Zero of the 98 live rows are that shape today — which is
 * data, not structure, and is precisely why the guard is here rather than in a
 * comment saying it cannot happen.
 *
 * THE OTHER CAUSE GETS NO SENTENCE FROM HERE, ON PURPOSE. A `stated_min` basis
 * means the stated minimum exceeds the stated maximum — a different problem with
 * a different remedy, and one the engine already names in its own `reason`
 * string and its own `stated_min_exceeds_stated_max` suppression bucket. It also
 * cannot reach this page: such a row returns `insufficient_data` before any
 * action is emitted, so it never carries the date pair. Measured: 3 such cells
 * estate-wide, 0 on the worklist. Guarded anyway — a structural guarantee that
 * nothing enforces is one refactor from being untrue.
 */
export function reorderPointAboveMaxNote(row) {
  const ev = (row && row.evidence) || {};
  if (!ev.reorder_point_above_max) return null;
  if (ev.reorder_point_above_max_basis !== 'lead_time_demand') return null;

  // Both dates the sentence relates must be ON the row. Presence only — which
  // of them is earlier is the ENGINE's finding, and re-deriving it here would
  // be a second expression of the one thing `reorder_point_above_max` says.
  const until = ev.projected_back_inside_max_date || ev.back_inside_max_date;
  if (!row.action_date || !until) return null;

  const pos = (v) => v !== null && v !== undefined
    && !Number.isNaN(Number(v)) && Number(v) > 0;
  const max = ev.max_qty;
  const rp = ev.reorder_point_qty;
  const lt = row.lead_time_days;
  if (!pos(max) || !pos(rp) || !pos(lt)) return null;

  return `Your max of ${formatQty(max)} is below the ${formatQty(rp)} you use `
    + `during its ${formatDays(lt)} lead time, so this reorders before it is `
    + `back under max. Raise the max above that.`;
}

/** The same instruction as one plain string. A JOIN of `actionParts`, never a
 *  second authoring of the sentence — two definitions is how one surface starts
 *  telling a buyer something the other does not. */
export function actionSentence(row) {
  const p = actionParts(row);
  return p ? `${p.lead}${p.strong}${p.tail}` : '';
}

/** Does this row carry an instruction a buyer can act on? The worklist shows
 *  these; everything else is context. Membership, never `!== 'no_action'`. */
export const ACTIONABLE = Object.freeze([
  'reduce_next_order_to', 'hold_until', 'increase_next_order_to',
]);

export function isActionable(row) {
  return !!row && ACTIONABLE.includes(row.action);
}

/**
 * Whether a safety value is an OBJECTION FOUND, as against a dimension that
 * could not be tested. Mirrors the calc layer's `SAFETY_BREACH_VALUES` exactly.
 *
 * Test membership here — NEVER `safety_check !== 'passes'`. `unverified` is
 * 55,606 of 64,867 rows on real PROD data, and treating it as an objection
 * would relabel roughly two thirds of the estate as safety breaches the module
 * never found.
 */
export const SAFETY_BREACH_VALUES = Object.freeze([
  'would_breach_min', 'would_breach_lead_time',
]);

export function isSafetyBreach(row) {
  return !!row && SAFETY_BREACH_VALUES.includes(row.safety_check);
}
